import jsPDF from 'jspdf';
import 'jspdf-autotable'; // Import the jsPDF AutoTable plugin  
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc'; // For time zone handling  
import timezone from 'dayjs/plugin/timezone'; // For time zone handling  
import * as XLSX from 'xlsx';

// Extend dayjs with plugins  
dayjs.extend(utc);
dayjs.extend(timezone);

// Utility function to map group size to type  
const mapSizeToType = (size) => {
    if (size === 2) return 'duet';
    if (size === 3) return 'trio';
    if (size >= 4 && size <= 9) return 'small group';
    if (size >= 10 && size <= 19) return 'large group';
    if (size >= 20) return 'production line';
    return 'solo';
};

export const generateGradingSheetPDF = (applications, venue) => {  
    const doc = new jsPDF('landscape'); // Landscape orientation for wider tables  
    console.log(applications);
    const header = (data) => {  
      doc.setFontSize(12);  
      doc.text('Judge Name: _______________________', 14, 10);  
      doc.text(`Venue: ${venue}`, doc.internal.pageSize.getWidth() - 80, 10);  
    };  
    
    // Define the columns (Number as the first column)  
    const tableColumn = [  
      'Number',  
      'Participants',  
      'Group Type',  
      'Event',  
      'Scheduled Start',  
      'Entertainment',  
      'Technicality',  
      'Style',  
      'Stage Presence',  
      'Originality',  
    ];  
    
    // Prepare the rows  
    const tableRows = [];  
    applications.forEach((application) => {  
      // Number  
      let number = '';  
      if (application.group && application.group.group_number) {  
        number = application.group.group_number;  
      } else if (application.contestant && application.contestant.contestant_number) {  
        number = application.contestant.contestant_number;  
      }  
    
      // Participants  
      let participants = '';  
      if (application.group && application.group.group_name) {  
        participants = application.group.group_name;  
      } else if (application.contestant) {  
        participants = `${application.contestant.first_name} ${application.contestant.last_name}`;  
      } else if (application.contestants && application.contestants.length > 0) {  
        participants = application.contestants  
          .map((contestant) => `${contestant.first_name} ${contestant.last_name}`)  
          .join(', ');  
      } else {  
        participants = 'No Contestants';  
      }  
    
      // Group Type  
      let groupType = '';  
      if (application.group && application.group.group_size) {  
        groupType = mapSizeToType(application.group.group_size);  
      } else {  
        groupType = 'solo';  
      }  
    
      // Event  
      const event = application.event ? `${application.event.name} - ${application.event.style}` : 'No Event';  
    
      // Scheduled Start  
      const date = application.date  
        ? dayjs.utc(application.date).tz('Europe/Bucharest').format('YYYY-MM-DD HH:mm')  
        : 'Not Scheduled';  
    
      // Empty fields for grades  
      const grades = ['', '', '', '', '', ''];  
    
      // Combine all data into a row  
      const row = [number, participants, groupType, event, date, ...grades];  
      tableRows.push(row);  
    });  
    
    // Add the table to the PDF  
    doc.autoTable({  
      head: [tableColumn],  
      body: tableRows,  
      startY: 30,  
      styles: {  
        fontSize: 8,  
        cellWidth: 'wrap',  
        halign: 'center',  
        valign: 'middle',  
        lineColor: [0, 0, 0],  
        lineWidth: 0.1,  
        fillColor: [255, 255, 255],  
        textColor: [0, 0, 0],  
      },  
      headStyles: {  
        fillColor: [255, 255, 255],  
        textColor: [0, 0, 0],  
        fontStyle: 'bold',  
        lineColor: [0, 0, 0],  
        lineWidth: 0.1,  
      },  
      alternateRowStyles: {  
        fillColor: [255, 255, 255],  
      },  
      tableLineColor: [0, 0, 0],  
      tableLineWidth: 0.1,  
      columnStyles: {  
        0: { cellWidth: 18 }, // Number  
        1: { cellWidth: 50 }, // Participants  
        2: { cellWidth: 25 }, // Group Type  
        3: { cellWidth: 35 }, // Event  
        4: { cellWidth: 30 }, // Scheduled Start  
      },  
      didDrawPage: header,  
    });  
    
    // Get the total number of pages  
    const pageCount = doc.internal.getNumberOfPages();  
    
    // Add page numbers to each page  
    for (let i = 1; i <= pageCount; i++) {  
      doc.setPage(i);  
      const pageSize = doc.internal.pageSize;  
      const pageHeight = pageSize.height ? pageSize.height : pageSize.getHeight();  
      doc.setFontSize(10);  
      doc.text(`Page ${i} / ${pageCount}`, pageSize.width - 20, pageHeight - 10);  
    }  
    
    // Save the PDF  
    doc.save(`${venue}_Grading_Sheet.pdf`);  
  };  

// Helper function to calculate the final grade    
export const calculateFinalGrade = (applicationGrades) => {
    const requiredFields = ['entertainment', 'technicality', 'style', 'stage_presence', 'originality'];
    return requiredFields.reduce((sum, field) => sum + (applicationGrades[field] || 0), 0);
};

export const exportGradedApplicationsToXLSX = (  
    gradedApplications,  
    grades,  
    selectedVenue,  
    user,  
    gradingFinished
  ) => {  
    const criteria = ['entertainment', 'technicality', 'style', 'stage_presence', 'originality'];  
    
    // Map data to an array of objects suitable for xlsx  
  const data = gradedApplications.map((app) => {  
      // Number  
      let number = '';  
      if (app.group && app.group.group_number) {  
        number = app.group.group_number;  
      } else if (app.contestant && app.contestant.contestant_number) {  
        number = app.contestant.contestant_number;  
      }  
    
      // Participants  
      let participants;  
      if (app.group && app.group.group_name) {  
        participants = app.group.group_name;  
      } else if (app.contestant) {  
        participants = `${app.contestant.first_name} ${app.contestant.last_name}`;  
      } else if (app.contestants && app.contestants.length > 0) {  
        participants = app.contestants  
          .map((contestant) => `${contestant.first_name} ${contestant.last_name}`)  
          .join(', ');  
      } else {  
        participants = 'No Contestants';  
      }  
    
      // Event  
      const event = app.event ? `${app.event.name} - ${app.event.style}` : 'No Event';  
    
      // Scheduled Start  
      const scheduledStart = app.date  
        ? dayjs.utc(app.date).tz('Europe/Bucharest').format('YYYY-MM-DD HH:mm')  
        : 'Not Scheduled';  
    
      const applicationGradesSaved = app.grades || [];
      if (applicationGradesSaved.length > 0) {
        const numJudges = applicationGradesSaved.length;
        const criteriaAverages = {};
        criteria.forEach((criterion) => {
          const total = applicationGradesSaved.reduce((acc, grade) => acc + (grade[criterion] || 0), 0);
          const average = total / numJudges;
          criteriaAverages[criterion] = average.toFixed(2);
        });
        const finalGrade = criteria
          .reduce((sum, criterion) => sum + parseFloat(criteriaAverages[criterion]), 0)
          .toFixed(2);

        const gradeColumns = {
          Entertainment: criteriaAverages.entertainment,
          Technicality: criteriaAverages.technicality,
          Style: criteriaAverages.style,
          'Stage Presence': criteriaAverages.stage_presence,
          Originality: criteriaAverages.originality,
        };

        return {
          Number: number,
          Participants: participants,
          Event: event,
          'Scheduled Start': scheduledStart,
          ...gradeColumns,
          'Final Grade': finalGrade,
        };
      }

      const applicationGrades = grades[app._id] || {};
      const finalGrade = calculateFinalGrade(applicationGrades);

      return {
        Number: number,
        Participants: participants,
        Event: event,
        'Scheduled Start': scheduledStart,
        Entertainment: applicationGrades.entertainment || '',
        Technicality: applicationGrades.technicality || '',
        Style: applicationGrades.style || '',
        'Stage Presence': applicationGrades.stage_presence || '',
        Originality: applicationGrades.originality || '',
        'Final Grade': finalGrade || '',
      };
    });  
    
    // Generate worksheet  
    const worksheet = XLSX.utils.json_to_sheet(data);  
    
    // Calculate column widths  
    const headerRow = Object.keys(data[0] || {});  
    const colWidths = headerRow.map((header) => ({ wch: header.length + 5 })); // Estimate initial width with some padding  
    
    data.forEach((row) => {  
      headerRow.forEach((header, index) => {  
        const cellValue = row[header] ? row[header].toString() : '';  
        if (cellValue.length + 2 > colWidths[index].wch) {  
          colWidths[index].wch = cellValue.length + 2;  
        }  
      });  
    });  
    
    // Set column widths  
    worksheet['!cols'] = colWidths;  
    
    // Create workbook and add the worksheet  
    const workbook = XLSX.utils.book_new();  
    const sheetName = gradingFinished  
      ? `${selectedVenue} Averaged Grades`  
      : `${user.first_name} ${user.last_name} Grades`;  
    XLSX.utils.book_append_sheet(workbook, worksheet, sheetName);  
    
    // Generate xlsx file and trigger download  
    const fileName = gradingFinished  
      ? `${selectedVenue}_Averaged_Grades.xlsx`  
      : `${user.first_name}_${user.last_name}_${selectedVenue}_Grades.xlsx`;  
    XLSX.writeFile(workbook, fileName);  
  };  